/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import OrbLoader from "../../components/common/OrbLoader";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReplayIcon from "@mui/icons-material/Replay";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { setToastMessage } from "../../store/slices/app/appSlice";
import {
  useGetInvoiceByIdQuery,
  useRetryInvoiceMutation,
  useDeleteInvoiceMutation,
} from "../../store/apis/invoiceApi";
import { useUserRole } from "../../hooks/useUserRole";
import {
  HoldedStatus,
  InvoiceClientSnapshot,
  InvoiceIssuerSnapshot,
} from "../../types/invoice";
import { formatEUR, formatDateES } from "../../utils/oc/ocCalc";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import PageContainer from "../../components/layout/PageContainer";
import { useGoBack } from "../../hooks/useGoBack";

const HoldedStatusChip: React.FC<{ status: HoldedStatus }> = ({ status }) => {
  const { t } = useTranslation();
  const color: "success" | "default" | "error" =
    status === "SENT" ? "success" : status === "ERROR" ? "error" : "default";
  return (
    <Chip
      color={color}
      label={t(`invoices.holdedStatus.${status}`)}
      sx={{ fontWeight: 600 }}
    />
  );
};

// Panel de datos fiscales congelados (snapshot).
const SnapshotPanel: React.FC<{
  title: string;
  snapshot: InvoiceClientSnapshot | InvoiceIssuerSnapshot;
  extra?: React.ReactNode;
}> = ({ title, snapshot, extra }) => {
  const { t } = useTranslation();
  const rows: Array<[string, string | undefined]> = [
    [t("invoices.detail.legalName"), snapshot.legalName],
    [t("invoices.detail.taxId"), snapshot.taxId],
    [t("invoices.detail.address"), snapshot.address],
    [t("invoices.detail.city"), snapshot.city],
    [t("invoices.detail.zip"), snapshot.zip],
    [t("invoices.detail.country"), snapshot.country],
    [t("invoices.detail.phone"), snapshot.phone],
    [t("invoices.detail.email"), snapshot.email],
    [t("invoices.detail.website"), snapshot.website],
  ];
  return (
    <Paper sx={{ p: 2, height: "100%" }}>
      <Typography variant="pMedium" sx={{ fontWeight: 600, mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="pSmall" sx={{ fontWeight: 700, mb: 1.5 }}>
        {snapshot.label}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {rows.map(([label, value]) => (
          <Box key={label} sx={{ display: "flex", gap: 1 }}>
            <Typography
              variant="pSmallest"
              sx={{ minWidth: 110, opacity: 0.7 }}
            >
              {label}
            </Typography>
            <Typography variant="pSmallest">{value || "—"}</Typography>
          </Box>
        ))}
        {extra}
      </Box>
    </Paper>
  );
};

const InvoiceDetail: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const collapsed = useSelector(selectSidebarCollapsed);
  const { isAdmin } = useUserRole();

  const {
    data: resp,
    isFetching,
    isError,
  } = useGetInvoiceByIdQuery(id ?? "", {
    skip: !id,
  });
  const invoice = resp?.data;

  const [retryInvoice, { isLoading: isRetrying }] = useRetryInvoiceMutation();
  const [deleteInvoice, { isLoading: isDeleting }] = useDeleteInvoiceMutation();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleRetry = async () => {
    if (!id) return;
    try {
      const r = await retryInvoice(id).unwrap();
      const detail = r.data;
      if (detail?.holdedStatus === "SENT") {
        dispatch(
          setToastMessage({
            message: t("invoices.detail.retrySuccess", {
              number: detail.holdedDocNumber || "",
            }),
            severity: "success",
          })
        );
      } else {
        dispatch(
          setToastMessage({
            message: detail?.holdedError || t("invoices.detail.retryFailed"),
            severity: "warning",
          })
        );
      }
    } catch (e: any) {
      // 502 — sigue en ERROR; el envelope viaja en e.data.data
      const msg =
        e?.data?.data?.holdedError ||
        e?.data?.message ||
        t("invoices.detail.retryFailed");
      dispatch(setToastMessage({ message: msg, severity: "warning" }));
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteInvoice(id).unwrap();
      dispatch(
        setToastMessage({
          message: t("invoices.history.deleted"),
          severity: "success",
        })
      );
      navigate("/invoices/history");
    } catch (e: any) {
      dispatch(
        setToastMessage({
          message: e?.data?.message || t("invoices.history.deleteError"),
          severity: "error",
        })
      );
    } finally {
      setConfirmDelete(false);
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "80px" : "250px",
        }}
      >
        <PageBreadcrumbs />

        <PageContainer>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => goBack("/invoices/history")}
              sx={{ textTransform: "none" }}
            >
              {t("invoices.detail.back")}
            </Button>
            {isAdmin && invoice && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                disabled={isDeleting}
                onClick={() => setConfirmDelete(true)}
                sx={{ textTransform: "none" }}
              >
                {t("invoices.history.deleteInvoice")}
              </Button>
            )}
          </Box>

          {isFetching ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <OrbLoader
                size="large"
                label={t("common.loading", { defaultValue: "Cargando…" })}
              />
            </Box>
          ) : isError || !invoice ? (
            <Alert severity="error">{t("invoices.detail.loadError")}</Alert>
          ) : (
            <>
              {/* Cabecera */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography variant="h4">
                      {invoice.holdedDocNumber ||
                        t("invoices.detail.noNumberYet")}
                    </Typography>
                    <Typography
                      variant="pSmall"
                      color={theme.palette.neutral["600"]}
                    >
                      {formatDateES(invoice.invoiceDate)}
                      {invoice.concept ? ` · ${invoice.concept}` : ""}
                    </Typography>
                    <Typography
                      variant="pSmall"
                      color={theme.palette.neutral["600"]}
                      sx={{ display: "block" }}
                    >
                      {t("invoices.detail.ocNumber")}: {invoice.ocNumber || "—"}
                    </Typography>
                  </Box>
                  <HoldedStatusChip status={invoice.holdedStatus} />
                </Box>

                {invoice.holdedStatus === "ERROR" && (
                  <Alert
                    severity="error"
                    sx={{ mt: 2 }}
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        startIcon={
                          isRetrying ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <ReplayIcon />
                          )
                        }
                        disabled={isRetrying}
                        onClick={handleRetry}
                        sx={{ textTransform: "none" }}
                      >
                        {t("invoices.detail.retry")}
                      </Button>
                    }
                  >
                    {invoice.holdedError || t("invoices.detail.retryFailed")}
                  </Alert>
                )}
              </Paper>

              {/* Snapshots */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <SnapshotPanel
                    title={t("invoices.detail.issuer")}
                    snapshot={invoice.issuerSnapshot}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SnapshotPanel
                    title={t("invoices.detail.client")}
                    snapshot={invoice.clientSnapshot}
                    extra={
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Typography
                          variant="pSmallest"
                          sx={{ minWidth: 110, opacity: 0.7 }}
                        >
                          {t("invoices.detail.holdedContactId")}
                        </Typography>
                        <Typography variant="pSmallest">
                          {invoice.clientSnapshot.holdedContactId || "—"}
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
              </Grid>

              {/* Líneas */}
              <Paper sx={{ mb: 3 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow
                        sx={{
                          "& th": {
                            fontWeight: 700,
                            bgcolor: theme.palette.neutral["100"],
                          },
                        }}
                      >
                        <TableCell>{t("invoices.table.concepto")}</TableCell>
                        <TableCell>{t("invoices.table.subtitulo")}</TableCell>
                        <TableCell align="right">
                          {t("invoices.table.subtotal")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoice.items.map((it, idx) => (
                        <TableRow key={`detail-item-${idx}`}>
                          <TableCell>{it.concepto}</TableCell>
                          <TableCell>{it.subtitulo || "—"}</TableCell>
                          <TableCell align="right">
                            {formatEUR(Number(it.subtotal) || 0)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    p: 2,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 3 }}>
                    <Typography variant="pMedium" sx={{ fontWeight: 700 }}>
                      {t("invoices.totals.total")}
                    </Typography>
                    <Typography variant="pMedium" sx={{ fontWeight: 700 }}>
                      {formatEUR(invoice.total)}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              <Typography
                variant="pSmallest"
                color={theme.palette.neutral["600"]}
              >
                {t("invoices.detail.createdBy", {
                  name: invoice.createdByName || "—",
                })}
              </Typography>
            </>
          )}
        </PageContainer>
      </Box>

      <ConfirmationModal
        open={confirmDelete}
        title={t("invoices.history.deleteTitle")}
        message={t("invoices.history.deleteMessage")}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
        isLoading={isDeleting}
      />
    </Box>
  );
};

export default InvoiceDetail;
